import { twMerge } from "tailwind-merge";
import { useEffect, useRef } from "react";
import { VideoUIActiveTab, VideoUIScrollPositions } from "../../videoUI.types";
import VideoUIChatContainer from "../VideoUIChat/VideoUIChatContainer";
import VideoUIMaterialsContainer from "../VideoUIMaterials/VideoUIMaterialsContainer";
import VideoUINavigation from "../VideoUINavigation/VideoUINavigation";
import VideoUIRosterContainer from "../VideoUIRoster/VideoUIRosterContainer";
import { PhaseAction } from "@platform/types/platform.types";
import { Article } from "@platform/features/support/support.types";
import { useChatStatusWithCircles } from "@platform/features/chat/hooks/useChatStatus";

export type Props = {
  activeTab: VideoUIActiveTab;
  scrollPositions: VideoUIScrollPositions;
  priorityBasedPolicy: boolean;
  sharingAttendeeId: string;
  onClose: () => void;
  onTabChange: (tab: VideoUIActiveTab) => void;
  onScrollPositionSave: (tab: VideoUIActiveTab, position: number) => void;
  meetingScriptUserPhaseAction: PhaseAction | null;
  unreadCount: number;
};

export function VideoUISidebar({
  activeTab,
  scrollPositions,
  priorityBasedPolicy,
  sharingAttendeeId,
  onClose,
  onTabChange,
  onScrollPositionSave,
  meetingScriptUserPhaseAction,
  unreadCount = 0,
}: Props) {
  const { isChatEnabled } = useChatStatusWithCircles();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const previousActiveTabRef = useRef<VideoUIActiveTab>(activeTab);

  // Save scroll position when tab changes
  useEffect(() => {
    const previousTab = previousActiveTabRef.current;
    const scrollContainer = scrollContainerRef.current;

    if (scrollContainer && previousTab !== activeTab) {
      // Restore the scroll position of the new active tab
      scrollContainer.scrollTop = scrollPositions[activeTab];

      previousActiveTabRef.current = activeTab;
    }
  }, [activeTab, scrollPositions]);

  // Also save scroll position when scrolling occurs
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      onScrollPositionSave(activeTab, scrollContainer.scrollTop);
    };

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, [activeTab, onScrollPositionSave]);

  return (
    <aside
      className={twMerge(
        "bg-brand-50 rounded-l-md shadow-[0px_0px_24px_0px_#050a00,_0_8px_4px_-2px_#050a00] max-w-[90dvw]",
        "fixed flex flex-col right-0 top-0 w-96 h-full",
        "sm:relative sm:w-96"
      )}
    >
      <VideoUINavigation
        activeTab={activeTab}
        priorityBasedPolicy={priorityBasedPolicy}
        sharingAttendeeId={sharingAttendeeId}
        onClose={onClose}
        onTabChange={onTabChange}
        unreadCount={unreadCount}
      />
      <div
        ref={scrollContainerRef}
        className="relative flex flex-1 flex-col gap-4 text-brand-800 overflow-y-auto"
      >
        {activeTab === "roster" && <VideoUIRosterContainer />}
        {activeTab === "chat" && isChatEnabled && <VideoUIChatContainer />}
        {activeTab === "materials" && (
          <VideoUIMaterialsContainer
            article={
              meetingScriptUserPhaseAction?.target_object_data as Article
            }
            scrollContainer={scrollContainerRef.current}
          />
        )}
      </div>
    </aside>
  );
}

export default VideoUISidebar;
